import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Image } from '@codes/ui-kit/components/ui/image';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { NameEditForm } from '@web/app/features/my-page/components';
import { Nav, useDialogStore, useSelectedStore } from '@web/app/shared';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { Sparkles, Trash2 } from 'lucide-react';

import { useOwnerStore } from '@codes/owner';
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from '@codes/ui-kit';
import { UserView, useWebCoreStore } from '@codes/web-core';

import SadIcon from '/assets/icons/sad.svg';
import SadDarkIcon from '/assets/icons/sad_dark.svg';

import { BillingsPage } from '../sub-pages/billings';
import { SubscriptionsPage } from '../sub-pages/subscriptions';

type TabValue = 'info' | 'payment';

export const MyPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const activeTab = (searchParams.get('tab') as TabValue) || 'info';

    const [isPreventDeletionDialogOpen, setIsPreventDeletionDialogOpen] = useState(false);
    const ownerInfo = useOwnerStore(state => state.owner);
    const userName = useWebCoreStore(state => state.userName);
    const email = useWebCoreStore(state => state.email);
    const updateUserName = useWebCoreStore(state => state.updateUserName);

    const { workspace } = useSelectedStore();
    const isProWorkspace = workspace && workspace.stereo === 'pro';

    const { openPriceDialog } = useDialogStore(state => state);

    const handleTabChange = (value: TabValue) => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', value);
        navigate(`?${params.toString()}`);
    };

    const handleSuccessUpdateName = (user: UserView) => {
        updateUserName(user);
        toast({ description: t('myPage.nameChangeComplete') });
    };

    return (
        <div className="w-full flex-1 flex flex-col animate-enter">
            {/* Upgrade Banner - full width */}
            {!isProWorkspace && (
                <div className="w-full bg-point/5 border-b border-point/10 h-12 flex items-center justify-center gap-2 text-sm font-medium">
                    <Sparkles className="w-4 h-4 text-point" />
                    <span className="text-text">{t('workspace.list.tryMore')}</span>
                    <Badge
                        onClick={openPriceDialog}
                        variant="B"
                        className="bg-point text-white ml-2 cursor-pointer px-3 text-xs font-medium capitalize hover:bg-point/90 transition-colors"
                    >
                        {t('workspace.list.upgrade')}
                    </Badge>
                </div>
            )}

            <PageContainer maxWidth="md" noPadding className="flex-1 flex flex-col">
                <Nav title={t('myPage.myPage')} />

                <Tabs defaultValue="payment" value={activeTab} onValueChange={handleTabChange}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="info" className="w-full">
                            {t('myPage.personalInfo')}
                        </TabsTrigger>
                        <TabsTrigger value="payment" className="w-full">
                            {t('myPage.paymentManagement')}
                        </TabsTrigger>
                        <TabsTrigger value="subscriptions" className="w-full">
                            {t('myPage.subscriptionManagement')}
                        </TabsTrigger>
                    </TabsList>

                    {/* ─── Personal Info Tab ─── */}
                    <TabsContent value="info" className="max-md:px-4 pb-20">
                        <div className="max-w-2xl mx-auto">
                            {/* Profile */}
                            <section>
                                <h2 className="text-xs font-semibold text-text-500 uppercase tracking-wider mb-4">
                                    {t('myPage.editInfo')}
                                </h2>
                                <div className="rounded-xl border border-border bg-card p-5">
                                    <NameEditForm
                                        currentName={userName}
                                        ownerId={ownerInfo?.id || ''}
                                        onSuccess={handleSuccessUpdateName}
                                    />
                                </div>
                            </section>

                            {/* Email */}
                            {email && (
                                <section className="mt-8">
                                    <h2 className="text-xs font-semibold text-text-500 uppercase tracking-wider mb-4">
                                        {t('myPage.email')}
                                    </h2>
                                    <div className="rounded-xl border border-border bg-card p-5">
                                        <div className="text-sm font-medium">{email}</div>
                                    </div>
                                </section>
                            )}

                            {/* Danger Zone */}
                            <section className="mt-10">
                                <h2 className="text-xs font-semibold text-error/80 uppercase tracking-wider mb-4">
                                    {t('myPage.dangerZone')}
                                </h2>
                                <div className="rounded-xl border border-error/20 bg-error/[0.02] p-5 flex items-center justify-between gap-4">
                                    <p className="text-sm text-text-500 flex-1">{t('myPage.deletionWarning')}</p>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <button className="shrink-0 text-xs text-error font-medium flex items-center gap-1.5 px-3 py-2 rounded-lg border border-error/20 hover:bg-error/5 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                                {t('myPage.accountDeletion')}
                                            </button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogTitle>{t('myPage.confirmAccountDeletion')}</AlertDialogTitle>
                                            <AlertDialogDescription className="text-error">
                                                {t('myPage.deletionWarning')}
                                            </AlertDialogDescription>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>{t('myPage.cancel')}</AlertDialogCancel>
                                                <AlertDialogAction onClick={() => setIsPreventDeletionDialogOpen(true)}>
                                                    {t('myPage.delete')}
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>

                                    {/* Prevention modal */}
                                    <AlertDialog
                                        open={isPreventDeletionDialogOpen}
                                        onOpenChange={setIsPreventDeletionDialogOpen}
                                    >
                                        <AlertDialogContent>
                                            <div className="flex-1 flex flex-col justify-center items-center">
                                                <AlertDialogTitle className="flex flex-col items-center gap-5">
                                                    <Image src={SadIcon} darkSrc={SadDarkIcon} />
                                                    <span className="text-lg">{t('myPage.leaveEureka')}</span>
                                                </AlertDialogTitle>
                                                <AlertDialogDescription className="text-center mt-2 whitespace-pre-wrap">
                                                    {t('myPage.rejoinNotice')}
                                                </AlertDialogDescription>
                                            </div>
                                            <AlertDialogFooter>
                                                <AlertDialogAction
                                                    className="flex-1"
                                                    onClick={() => {
                                                        toast({ title: t('myPage.deletionComplete') });
                                                        setIsPreventDeletionDialogOpen(false);
                                                    }}
                                                >
                                                    {t('myPage.confirm')}
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </section>
                        </div>
                    </TabsContent>

                    {/* ─── Payment Tab ─── */}
                    <TabsContent value="payment" className="max-md:px-4 pb-20">
                        <BillingsPage />
                    </TabsContent>

                    {/* ─── Subscriptions Tab ─── */}
                    <TabsContent value="subscriptions" className="max-md:px-4 pb-20">
                        <SubscriptionsPage />
                    </TabsContent>
                </Tabs>
            </PageContainer>
        </div>
    );
};
